'use client';
import { hasValue } from '@woonstad/helpers-utils';
import clsx from 'clsx';
import React from 'react';
import type { Accept, DropzoneOptions } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';

import { BasicPlus } from '../../../icons/basic';
import { Typography } from '../../typography/Typography';
import { useFormField } from '../FormField';
import { filesMatch } from './upload-shared/fileIdentity';
import { UploadedFile } from './upload-shared/UploadedFile';

export { acceptDocuments, acceptImages } from './acceptFileTypes';

type Props = {
  accept: Accept;
  maxFiles?: number;
  chooseFileText?: string;
  dropFileText?: string;
  capture?: boolean | 'user' | 'environment';
  transformFiles?: (files: File[]) => Promise<File[]>;
  showPreview?: boolean;
  hideUploadButton?: boolean;
};

export const Upload = React.forwardRef<HTMLDivElement, Props>(({
  accept,
  maxFiles = 1,
  chooseFileText,
  dropFileText,
  capture,
  transformFiles,
  showPreview,
  hideUploadButton,
}, ref) => {
  const {
    field,
    fieldState,
    name,
    setValue,
  } = useFormField();

  const onDrop: DropzoneOptions['onDrop'] = async (acceptedFiles) => {
    const files = await Promise.all(acceptedFiles.map(async (file) => {
      if (hasValue(transformFiles)) {
        const transformedFile = (await transformFiles([file]))[0];

        if (hasValue(transformedFile)) {
          return transformedFile;
        }
      }

      return file;
    }));

    const filesWithValues = files.filter((file) => file instanceof File);

    setValue(
      name,
      [
        ...(maxFiles !== 1 && field?.value ? field.value : []),
        ...filesWithValues,
      ],
      {
        shouldValidate: true,
        shouldDirty: true,
      },
    );
  };

  const handleDelete = (file: File) => {
    const deletedFileIndex = field?.value.findIndex((f: File) => filesMatch(f, file));

    const newFiles = field?.value.toSpliced(deletedFileIndex, 1);

    setValue(
      name,
      newFiles,
      {
        shouldValidate: true,
        shouldDirty: true,
      },
    );
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isFileDialogActive,
  } = useDropzone(
    {
      accept: accept,
      multiple: maxFiles > 1 || false,
      maxFiles: maxFiles,
      onDrop,
    },
  );

  const isDisabled = field?.disabled || field?.value?.length >= maxFiles;
  const isActive = isFileDialogActive || isDragActive;
  const isHidden = hideUploadButton && field?.value?.length >= maxFiles;

  const rootClasses = clsx('relative flex h-14 w-full', {
    'bg-upload-enabled border border-dashed border-upload-enabled text-upload-enabled cursor-pointer hover:border-solid':
      !isDisabled && !fieldState.error && !isActive,
    'bg-upload-dragging border-2 border-solid border-upload-dragging text-upload-dragging cursor-pointer':
      !isDisabled && !fieldState.error && isActive,
    'bg-upload-error border border-upload-error text-upload-error': fieldState.error,
    'bg-upload-disabled text-upload-disabled': isDisabled,
    'hidden': isHidden,
  });

  if (
    !fieldState.error
    && maxFiles === 1
    && field?.value?.[0]
    && field?.value?.[0] instanceof File
  ) {
    return (
      <UploadedFile
        onDelete={handleDelete}
        file={field?.value[0]}
        showPreview={showPreview}
      />
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-2">
      {field?.value?.flatMap((file: File, index: number) => {
        if (file instanceof File) {
          return (
            <UploadedFile
              error={Array.isArray(fieldState.error) ? fieldState.error[index] : undefined}
              onDelete={handleDelete}
              key={file.name + file.lastModified}
              file={file}
              showPreview={showPreview}
            />
          );
        }

        return [];
      })}
      <div
        className={rootClasses}
        {...(isDisabled ? {} : getRootProps())}
        ref={ref}
      >
        {!isDisabled && (
          <input
            {...getInputProps({
              name,
              capture,
              style: {
                border: 0,
                opacity: 0,
                display: 'block',
                height: '100%',
                padding: '0px',
                cursor: 'pointer',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                whiteSpace: 'nowrap',
              },
            })}
          />
        )}
        <div className="pointer-events-none flex w-full items-center justify-center">
          {isDragActive && (
            <Typography variant="content-body-bold">
              {dropFileText ? dropFileText : 'Laat bestand los om te uploaden...'}
            </Typography>
          )}
          {!isDragActive && (
            <>
              <BasicPlus className="mr-3 size-4" />
              <Typography variant="label-regular">{chooseFileText ? chooseFileText : 'Kies bestand...'}</Typography>
            </>
          )}
        </div>
      </div>
    </div>
  );
});

Upload.displayName = 'Upload';
